// The Accounts tab (`/accounts`, design `#s01`). Rewritten by WS-2 per the
// 2026-09-06 UX execution plan §7.3/WS-2: needs-attention strip → net-worth
// header (+ collapsed trend link) → connection groups → Assets/Debts/Net
// worth summary → Connect a bank / Add manual account. Band-adaptive via
// `useShellContext().band` rather than a Tailwind breakpoint, per the
// responsive plan (§10): state, order and actions never change across
// bands, only the composition (stacked cards vs. a dense table plus a
// persistent trend pane).
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRef, useState, type ReactNode } from 'react';
import type { Account, CanonicalAccount } from '@/api/hooks/useAccounts';
import { useNetWorthSeries } from '@/api/hooks/useNetWorthSeries';
import { useShellContext } from '@/app/shell/AppShell';
import { scopeHref } from '@/app/shell/scope';
import { Sparkline } from '@/components/charts/Sparkline';
import { StatusDot } from '@/components/StatusDot';
import { NeedsAttentionStrip } from './NeedsAttentionStrip';
import { NetWorthHeader } from './NetWorthHeader';
import { AccountsSummaryRows } from './AccountsSummaryRows';
import { AccountsTable } from './AccountsTable';
import { ConnectionGroup, groupAccountsByConnection } from './ConnectionGroup';
import { computeNetWorthRollup } from './netWorthRollup';
import { groupCanonicalAccountsForPresentation } from './ConnectionGroup';
import { DateScopeGuard } from '@/app/shell/DateScopeGuard';
import { ReconciliationReviewPanel, type ReconciliationReviewPanelProps } from '@/features/reconciliation/ReconciliationReviewPanel';
import { classifyReviewLaunch } from '@/features/reconciliation/reviewLaunch';
import { ManualAccountEditor } from './ManualAccountEditor';
import { ManualBalancePanel } from './ManualBalancePanel';

function AccountsEmptyState({ savedHistory = false, primary = false }: { savedHistory?: boolean; primary?: boolean }) {
  const navigate = useNavigate();
  if (savedHistory) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center gap-4 px-6 py-16 text-center" data-testid="saved-history-empty">
        <h1 tabIndex={-1} className="text-title font-semibold">No saved account history is available.</h1>
        <p className="text-muted-foreground">This read-only history snapshot has no accounts to show.</p>
      </div>
    );
  }
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center gap-6 px-6 py-16 text-center">
      {primary ? (
        <p className="text-title font-semibold">
          "…a business trip to LA in July"
        </p>
      ) : (
        <h1 tabIndex={-1} className="text-title font-semibold">
        "…a business trip to LA in July"
        </h1>
      )}
      <p className="text-muted-foreground">
        Connect a bank and MoneySkyline can answer questions like that about your own ledger.
      </p>
      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/connect')}
          className="min-h-11 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          Connect a bank
        </button>
        <button
          type="button"
          onClick={() => navigate('/connect?manual=1')}
          className="min-h-11 text-sm text-muted-foreground underline"
        >
          Add manual account
        </button>
      </div>
    </div>
  );
}

function DiagnosticScopeNotice() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.delete('scope');
  const query = params.toString();
  const canonicalHref = `${location.pathname}${query ? `?${query}` : ''}${location.hash}`;

  return (
    <div role="status" aria-live="polite" className="mx-auto max-w-5xl px-4 pt-6">
      <p className="rounded-md border border-degraded-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        Diagnostic view: physical account records for the requested scope. Totals here are not the canonical net worth.
      </p>
      <Link to={canonicalHref} className="mt-2 inline-flex min-h-11 items-center text-sm font-semibold underline">
        Return to canonical Accounts view
      </Link>
    </div>
  );
}

function CanonicalTrendLink() {
  const location = useLocation();
  return (
    <Link
      to={scopeHref(`/accounts/trend${location.hash}`, location.search)}
      className="mt-3 flex min-h-11 flex-col gap-2 border-t border-border pt-2 text-sm"
      data-testid="collapsed-trend-link"
    >
      <span className="flex items-center justify-between text-foreground">
        Trend &amp; movers <span aria-hidden="true">›</span>
      </span>
    </Link>
  );
}

function CollapsedTrend({ accounts }: { accounts: Account[] }) {
  const { range } = useShellContext();
  const series = useNetWorthSeries(range);
  const rollup = computeNetWorthRollup(accounts);
  if (rollup.total === null) return null;

  const trend = series.data?.status === 'ok' ? series.data.data : null;

  return (
    <Link
      to="/accounts/trend"
      className="mt-3 flex min-h-11 flex-col gap-2 border-t border-border pt-2 text-sm"
      data-testid="collapsed-trend-link"
    >
      {trend && (
        <>
          <Sparkline points={trend.points} />
          <div className="grid grid-cols-3 gap-2 text-xs">
            <span>Today {trend.deltas.today.value}</span>
            <span>Week {trend.deltas.week.value}</span>
            <span>Month {trend.deltas.month.value}</span>
          </div>
        </>
      )}
      <span className="flex items-center justify-between text-foreground">
        Trend &amp; movers <span aria-hidden="true">›</span>
      </span>
    </Link>
  );
}

/** One named status line per reconnected account, grouped and labelled.
 *
 * Each child already carries `role="status"`, so the group deliberately has no
 * `aria-live` of its own: nesting live regions makes screen readers announce
 * the same update twice.
 */
function ReconnectStatusStrips({ accounts }: { accounts: CanonicalAccount[] }) {
  const location = useLocation();
  const reconnected = accounts.filter((account) => account.reconnect_match_status !== null);
  if (reconnected.length === 0) return null;
  return (
    <section data-testid="reconnect-status" aria-labelledby="reconnect-status-heading" className="space-y-2">
      <h2 id="reconnect-status-heading" className="text-sm font-medium text-muted-foreground">Reconnected accounts</h2>
      {reconnected.map((account) => {
        const imported = account.reconnect_imported_count;
        const skipped = account.reconnect_skipped_retained_count;
        // Uncertainty is a pending decision, not missing data, so it is
        // answered before any resume-status branch and never says
        // "unavailable". The name is inside the sentence here because the
        // sentence is about that account by name.
        if (account.reconnect_match_status === 'auto_match_uncertain') {
          return (
            <p key={account.account_id} role="status" className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-degraded-border px-3 py-2 text-sm">
              <span>{account.display_name} was added as a new account. Confirm whether it is the same as your existing one.</span>
              {account.canonical_account_id !== null && (
                <Link to={scopeHref(`/accounts${location.hash}`, location.search, { account: account.canonical_account_id })} className="inline-flex min-h-11 items-center text-primary underline">
                  Review this match
                </Link>
              )}
            </p>
          );
        }
        const body = (() => {
          if (account.reconnect_resume_status === 'complete' && account.reconnect_breakpoint_date !== null && imported !== null && skipped !== null) {
            // The breakpoint date is the last day NOT imported, so the copy
            // says "after", and the counts avoid the data model's vocabulary.
            const date = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(`${account.reconnect_breakpoint_date}T00:00:00`));
            return `Resumed after ${date}: ${imported} imported, ${skipped} already in your history`;
          }
          if (account.reconnect_resume_status === 'running') {
            return imported !== null && skipped !== null
              ? `Resume in progress: ${imported} imported, ${skipped} already in your history known so far`
              : 'Resume in progress.';
          }
          if (account.reconnect_resume_status === 'failed') {
            // The next sync closes out a failed bootstrap, so this promises
            // something the product actually does rather than asking the user
            // to chase it.
            return 'Resume did not complete. Your existing history is unchanged. It will be retried on the next sync.';
          }
          return 'Resume information is unavailable.';
        })();
        const degraded = account.reconnect_resume_status === 'failed' || account.reconnect_resume_status === null;
        return (
          <p key={account.account_id} role="status" className={`rounded-md border px-3 py-2 text-sm ${degraded ? 'border-degraded-border' : 'border-border'}`}>
            <span className="font-medium">{account.display_name}</span>{' '}
            <span>{body}</span>
          </p>
        );
      })}
    </section>
  );
}

function CanonicalNeedsAttentionStrip({ accounts }: { accounts: CanonicalAccount[] }) {
  const location = useLocation();
  const disconnected = accounts.filter((account) => account.status_detail === 'connection_disconnected');
  const connected = accounts.filter((account) => account.status_detail !== 'connection_disconnected');
  const institutionNames = (rows: CanonicalAccount[], status: CanonicalAccount['status']) =>
    Array.from(new Set(rows.filter((account) => account.status === status && account.source !== 'manual').map((account) => account.institution_name ?? 'Unknown institution')));
  const needsLogin = institutionNames(connected, 'needs_login');
  const errored = institutionNames(connected, 'error');
  const disconnectedNames = institutionNames(disconnected, 'error');
  const staleCount = accounts.filter((account) => account.balance !== null && account.balance_status === 'stale').length;
  const derivedCount = accounts.filter((account) => account.balance !== null && account.balance_status === 'derived').length;
  const unavailableCount = accounts.filter((account) => account.balance === null).length;
  if (disconnectedNames.length === 0 && needsLogin.length === 0 && errored.length === 0 && staleCount === 0 && derivedCount === 0 && unavailableCount === 0) return null;

  const parts = [
    disconnectedNames.length > 0 ? `${disconnectedNames.join(', ')} disconnected` : '',
    needsLogin.length > 0 ? `${needsLogin.join(', ')} needs login` : '',
    errored.length > 0 ? `${errored.join(', ')} sync failed` : '',
    staleCount > 0 ? `${staleCount} stale` : '',
    derivedCount > 0 ? `${derivedCount} balance needs review` : '',
    unavailableCount > 0 ? `${unavailableCount} balance unavailable` : '',
  ].filter(Boolean);

  return (
    <div className="mb-4 flex min-h-11 items-center gap-2 rounded-lg border border-degraded-border bg-degraded-surface px-3 py-2 text-xs text-degraded-foreground" data-testid="needs-attention-strip" role="status">
      <StatusDot tone="warn" shape="filled" label="Needs attention" />
      <span className="flex-1">{parts.join(' · ')}</span>
      {(needsLogin.length > 0 || errored.length > 0) && <Link to={scopeHref(`/connect${location.hash}`, location.search)} className="flex min-h-11 items-center font-semibold text-degraded-foreground">Reconnect</Link>}
    </div>
  );
}

function CanonicalAccountsBody({ canonicalAccounts, manualAccount }: { canonicalAccounts: CanonicalAccount[]; manualAccount?: CanonicalAccount }) {
  const { band, dateScopeAvailability } = useShellContext();
  const presentation = groupCanonicalAccountsForPresentation(canonicalAccounts);
  if (presentation.state === 'inconsistent') {
    return <p role="status" data-testid="canonical-account-presentation-inconsistent">Account history is unavailable. Try again.</p>;
  }

  return (
    <div data-testid="canonical-accounts-body" data-viewport-band={band}>
      <CanonicalNeedsAttentionStrip accounts={canonicalAccounts} />

      <ReconnectStatusStrips accounts={canonicalAccounts} />

      <div className="space-y-4">
        <section className="rounded-lg border border-border bg-card p-4">
          <NetWorthHeader accounts={canonicalAccounts} aggregateDisplay="canonical_unavailable" />
          <DateScopeGuard availability={dateScopeAvailability}>
            <CanonicalTrendLink />
          </DateScopeGuard>
        </section>

        {band === 'mobile' ? (
          <div className="space-y-4">
            {presentation.groups.map((group) => (
              <ConnectionGroup key={group.key} group={group} aggregateDisplay="canonical_unavailable" />
            ))}
            <AccountsSummaryRows accounts={canonicalAccounts} aggregateDisplay="canonical_unavailable" />
          </div>
        ) : (
          <AccountsTable canonicalAccounts={canonicalAccounts} showType={band === 'desktop'} />
        )}
      </div>

      {manualAccount && (
        <div className="mt-4">
          <ManualBalancePanel accountId={manualAccount.account_id} />
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        Manual accounts are included in this account view. Removing a connection keeps its history.
      </p>
    </div>
  );
}

function getSelectedCanonicalAccount(accounts: CanonicalAccount[], search: string) {
  const selectedIds = [...new Set(new URLSearchParams(search).getAll('account').map((value) => value.trim()).filter(Boolean))];
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  if (!selectedId) return null;
  const matches = accounts.filter((account) => account.canonical_account_id === selectedId
    || (account.canonical_account_id === null && account.reconciliation_initialization_required && account.account_id === selectedId));
  return matches.length === 1 ? matches[0] : null;
}

function CanonicalReviewControl({
  eligibility,
  primary = false,
  onOpen,
  openerRef,
}: {
  eligibility: ReturnType<typeof classifyReviewLaunch> | null;
  primary?: boolean;
  onOpen: () => void;
  openerRef: React.MutableRefObject<HTMLButtonElement | null>;
}) {
  if (!eligibility) return null;
  if (eligibility.kind !== 'eligible') {
    const copy = eligibility.kind === 'no_target'
      ? 'Account review is not available for this account.'
      : eligibility.kind === 'ambiguous'
        ? 'Account review needs a single active connection.'
        : 'Account review needs setup.';
    return <p role="status" className="flex min-h-11 items-center text-sm text-muted-foreground">{copy}</p>;
  }
  // An unsettled match is this account's primary next action, so it is styled
  // and ordered as one rather than sharing the outline treatment.
  return (
    <button
      ref={(node) => { openerRef.current = node; }}
      type="button"
      onClick={onOpen}
      className={primary
        ? 'flex min-h-11 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground'
        : 'flex min-h-11 items-center rounded-md border border-input px-4 text-sm'}
    >
      {primary ? 'Review uncertain account match' : 'Review account changes'}
    </button>
  );
}

function PrimaryAccountsFrame({ children, showActions = false, reviewEligibility, reviewIdentity, manualAccount, reviewPrimary = false }: {
  children: ReactNode;
  showActions?: boolean;
  reviewEligibility?: ReturnType<typeof classifyReviewLaunch>;
  reviewIdentity?: string;
  manualAccount?: CanonicalAccount;
  reviewPrimary?: boolean;
}) {
  const location = useLocation();
  const [openReviewSession, setOpenReviewSession] = useState<string | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const manualEditorOpenerRef = useRef<HTMLButtonElement | null>(null);
  const [manualEditorOpen, setManualEditorOpen] = useState(false);
  const currentReviewIdentity = reviewIdentity ?? 'none';
  const [previousReviewIdentity, setPreviousReviewIdentity] = useState(currentReviewIdentity);
  if (previousReviewIdentity !== currentReviewIdentity) {
    setPreviousReviewIdentity(currentReviewIdentity);
    setOpenReviewSession(null);
  }
  const currentManualAccountId = manualAccount?.account_id ?? 'none';
  const [previousManualAccountId, setPreviousManualAccountId] = useState(currentManualAccountId);
  if (previousManualAccountId !== currentManualAccountId) {
    setPreviousManualAccountId(currentManualAccountId);
    setManualEditorOpen(false);
  }
  const launchKey = reviewEligibility?.kind === 'eligible'
    ? `${reviewEligibility.launch.canonicalAccountId}:${reviewEligibility.launch.replacementItemId}`
    : null;
  const reviewSession = launchKey ? `${currentReviewIdentity}:${launchKey}` : null;
  const panelProps: ReconciliationReviewPanelProps | null = reviewEligibility?.kind === 'eligible' && reviewSession
    ? {
      launch: reviewEligibility.launch,
      open: openReviewSession === reviewSession,
      onOpenChange: (open) => setOpenReviewSession(open ? reviewSession : null),
      returnFocusRef: openerRef,
    }
    : null;
  const reviewControl = (
    <CanonicalReviewControl
      eligibility={reviewEligibility ?? null}
      primary={reviewPrimary}
      onOpen={() => reviewSession && setOpenReviewSession(reviewSession)}
      openerRef={openerRef}
    />
  );
  return (
    <section className="mx-auto max-w-5xl px-4 py-6" data-testid="accounts-primary-frame">
      <div className="mb-4 flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
        <h1 tabIndex={-1} className="text-title font-semibold">Accounts</h1>
        {showActions && (
          <div className="flex flex-wrap gap-2" data-testid="accounts-action-row">
            {/* An unsettled match leads the row: it is the account's next action. */}
            {reviewPrimary && reviewControl}
            {/* Only one filled button at a time: while an unsettled match is
                this account's next action, connecting another bank steps back
                to the outline treatment. */}
            <Link to={scopeHref(`/connect${location.hash}`, location.search)} className={reviewPrimary
              ? 'flex min-h-11 items-center rounded-md border border-input px-4 text-sm'
              : 'flex min-h-11 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground'}>Connect a bank</Link>
            <Link to={scopeHref(`/connect?manual=1${location.hash}`, location.search)} className="flex min-h-11 items-center rounded-md border border-input px-4 text-sm">Add manual account</Link>
            {manualAccount && (
              <button
                ref={manualEditorOpenerRef}
                type="button"
                onClick={() => setManualEditorOpen(true)}
                className="flex min-h-11 items-center rounded-md border border-input px-4 text-sm"
              >
                Edit account details
              </button>
            )}
            {!reviewPrimary && reviewControl}
          </div>
        )}
      </div>
      {panelProps ? <ReconciliationReviewPanel {...panelProps} /> : null}
      {manualAccount && (
        <ManualAccountEditor
          account={manualAccount}
          open={manualEditorOpen}
          onOpenChange={(open) => {
            setManualEditorOpen(open);
            if (!open) manualEditorOpenerRef.current?.focus();
          }}
        />
      )}
      {children}
    </section>
  );
}

function AccountsBody({ accounts, savedHistory = false }: { accounts: Account[]; savedHistory?: boolean }) {
  const { band, dateScopeAvailability } = useShellContext();
  const rollup = computeNetWorthRollup(accounts);
  const groups = groupAccountsByConnection(accounts);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-viewport-band={band}>
      {/* Phone is one column per the design's #s01 note, so the actions stack
          under the heading; from tablet up they sit inline on the right. A
          single row at 320px left the heading and the primary button flush at
          the same x with no gap, which read as overlapping text. */}
      <div className="mb-4 flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
        <h1 tabIndex={-1} className="text-title font-semibold">
          Accounts
        </h1>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/connect"
            className="flex min-h-11 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground"
          >
            Connect a bank
          </Link>
          <Link
            to="/connect?manual=1"
            className="flex min-h-11 items-center rounded-md border border-input px-4 text-sm"
          >
            Add manual account
          </Link>
        </div>
      </div>

      {savedHistory && (
        <p className="mb-4 rounded-md border border-degraded-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground" data-testid="saved-history-label">
          Saved history · read-only snapshot. Current totals are unavailable here.
        </p>
      )}

      <NeedsAttentionStrip accounts={accounts} rollup={rollup} className="mb-4" />

      <div className="space-y-4">
        {!savedHistory && (
          <section className="rounded-lg border border-border bg-card p-4">
            <NetWorthHeader accounts={accounts} />
            <DateScopeGuard availability={dateScopeAvailability}>
              <CollapsedTrend accounts={accounts} />
            </DateScopeGuard>
          </section>
        )}

        {band === 'mobile' ? (
          <div className="space-y-4">
            {groups.map((group) => (
              <ConnectionGroup key={group.key} group={group} />
            ))}
            {!savedHistory && <AccountsSummaryRows accounts={accounts} />}
          </div>
        ) : (
          <AccountsTable accounts={accounts} showType={band === 'desktop'} showSummary={!savedHistory} />
        )}
      </div>

      {!savedHistory && (
        <p className="mt-4 text-xs text-muted-foreground">
          Manual accounts are included in this total and use their last reported balance date. Removing a
          connection keeps its history.
        </p>
      )}
    </div>
  );
}

export function AccountsPage() {
  const { readScope, scopeAvailability, currentAccounts, savedAccounts, canonicalAccounts, retryScopeAvailability } = useShellContext();
  const location = useLocation();

  if (scopeAvailability === 'pending' || (scopeAvailability === 'ready' && readScope === null && canonicalAccounts === undefined)) {
    if (readScope === null) {
      return (
        <PrimaryAccountsFrame>
          <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
            <p className="text-sm text-muted-foreground">Loading accounts…</p>
          </div>
        </PrimaryAccountsFrame>
      );
    }
    return (
      <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
        <p className="text-sm text-muted-foreground">Loading accounts…</p>
      </div>
    );
  }

  if (scopeAvailability === 'error') {
    if (readScope === null) {
      return (
        <PrimaryAccountsFrame>
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center" data-testid="accounts-scope-error">
            <p className="text-2xl font-bold">Accounts are temporarily unavailable</p>
            <p className="max-w-md text-muted-foreground">We could not load your account history. Try again.</p>
            <button type="button" onClick={retryScopeAvailability} className="inline-flex min-h-11 items-center rounded bg-primary px-4 py-2 text-primary-foreground">Retry</button>
          </div>
        </PrimaryAccountsFrame>
      );
    }
    return (
      <main className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center" data-testid="accounts-scope-error">
        <h1 tabIndex={-1} className="text-2xl font-bold">Accounts are temporarily unavailable</h1>
        <p className="max-w-md text-muted-foreground">We could not load your account history. Try again.</p>
        <button type="button" onClick={retryScopeAvailability} className="inline-flex min-h-11 items-center rounded bg-primary px-4 py-2 text-primary-foreground">
          Retry
        </button>
      </main>
    );
  }

  if (readScope === null) {
    if (!canonicalAccounts || canonicalAccounts.length === 0) {
      return (
        <PrimaryAccountsFrame showActions>
          <AccountsEmptyState primary />
        </PrimaryAccountsFrame>
      );
    }
    const selected = getSelectedCanonicalAccount(canonicalAccounts, location.search);
    const eligibility = selected ? classifyReviewLaunch(selected) : null;
    const reviewIdentity = eligibility?.kind === 'eligible'
      ? `${eligibility.launch.canonicalAccountId}:${eligibility.launch.replacementItemId}`
      : eligibility?.kind ?? 'none';
    const manualAccount = selected?.source === 'manual' ? selected : undefined;
    return <PrimaryAccountsFrame showActions reviewEligibility={eligibility ?? undefined} reviewIdentity={reviewIdentity} manualAccount={manualAccount} reviewPrimary={eligibility?.kind === 'eligible' && eligibility.uncertain}>
      <CanonicalAccountsBody canonicalAccounts={canonicalAccounts} manualAccount={manualAccount} />
    </PrimaryAccountsFrame>;
  }

  const savedHistory = readScope === 'saved_history';
  const accounts = (savedHistory ? savedAccounts : currentAccounts) ?? [];

  if (accounts.length === 0) {
    return (
      <>
        <DiagnosticScopeNotice />
        <AccountsEmptyState savedHistory={savedHistory} />
      </>
    );
  }

  return (
    <>
      <DiagnosticScopeNotice />
      <AccountsBody accounts={accounts} savedHistory={savedHistory} />
    </>
  );
}

export default AccountsPage;
